// Anonymous Purchase Restriction Modal
// 
// This modal blocks the shopping experience when:
// 1. Collection has metafield `public.allow_anonymous_purchase` set to false
// 2. No svsCustomerId is found in URL params or localStorage
//
// Usage: The modal is automatically initialized and triggered based on collection metafield settings

if (!window.AnonymousPurchaseModal) {
class AnonymousPurchaseModal {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.backdrop = null;
    this.createModal();
    this.bindEvents();
  }

  createModal() {
    // Create backdrop for blur effect
    this.backdrop = document.createElement('div');
    this.backdrop.id = 'anonymous-purchase-backdrop';
    this.backdrop.className = 'anonymous-purchase-backdrop';

    // Create modal HTML structure
    this.modal = document.createElement('div');
    this.modal.id = 'anonymous-purchase-modal';
    this.modal.className = 'anonymous-purchase-modal';
    this.modal.innerHTML = `
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-shop-logo">
              <img id="modal-logo" src="" alt="Shop Logo" style="display: none;" />
            </div>
            <h2 id="modal-title">Additional Information Required</h2>
          </div>
          <div class="modal-body">
            <div class="modal-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div id="modal-message">
              <!-- Message will be populated by JavaScript -->
            </div>
          </div>
        </div>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .anonymous-purchase-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        background: rgba(0, 0, 0, 0.3);
        z-index: 999;
        display: none;
      }
      
      .anonymous-purchase-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1000;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 20px;
        pointer-events: none;
      }
      
      .anonymous-purchase-modal.is-open { 
        display: flex; 
        pointer-events: auto;
      }
      
      .anonymous-purchase-backdrop.is-open { 
        display: block; 
      }
      
      .anonymous-purchase-modal .modal-dialog {
        background: rgb(var(--color-background));
        border-radius: 12px;
        max-width: 500px;
        width: 100%;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(var(--color-foreground), 0.1);
        animation: modalSlideIn 0.3s ease-out;
      }
      
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: translateY(-20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      .anonymous-purchase-modal .modal-header {
        padding: 30px 30px 20px;
        text-align: center;
        border-bottom: 1px solid rgba(var(--color-foreground), 0.1);
      }
      
      .anonymous-purchase-modal .modal-shop-logo {
        margin-bottom: 15px;
      }
      
      .anonymous-purchase-modal .modal-shop-logo img {
        max-height: 60px;
        max-width: 200px;
        height: auto;
        width: auto;
        object-fit: contain;
      }
      
      .anonymous-purchase-modal .modal-header h2 { 
        margin: 0; 
        font-size: 24px;
        font-weight: 600;
        color: rgb(var(--color-foreground));
      }
      
      .anonymous-purchase-modal .modal-body { 
        padding: 30px;
        text-align: center;
      }
      
      .anonymous-purchase-modal .modal-icon {
        margin-bottom: 20px;
        color: rgb(var(--color-button));
      }
      
      .anonymous-purchase-modal .modal-icon svg {
        width: 64px;
        height: 64px;
      }
      
      .anonymous-purchase-modal .modal-body p {
        margin: 0;
        font-size: 16px;
        line-height: 1.6;
        color: rgba(var(--color-foreground), 0.8);
      }
      
      /* Minimal blur effect - customers can clearly see the shop */
      body.anonymous-purchase-blocked #MainContent {
        filter: blur(0.5px);
        pointer-events: none;
        user-select: none;
        opacity: 0.8;
      }
      
      body.anonymous-purchase-blocked .shopify-section-header {
        pointer-events: none;
        opacity: 0.9;
      }
      
      body.anonymous-purchase-blocked .shopify-section-footer {
        pointer-events: none;
        opacity: 0.9;
      }
      
      /* Prevent scrolling when modal is open */
      body.anonymous-purchase-blocked {
        overflow: hidden;
      }
      
      @media (max-width: 768px) {
        .anonymous-purchase-modal { 
          padding: 15px; 
        }
        .anonymous-purchase-modal .modal-dialog { 
          max-width: 100%; 
        }
        .anonymous-purchase-modal .modal-header {
          padding: 25px 20px 15px;
        }
        .anonymous-purchase-modal .modal-body {
          padding: 25px 20px;
        }
        .anonymous-purchase-modal .modal-header h2 {
          font-size: 20px;
        }
        .anonymous-purchase-modal .modal-body p {
          font-size: 15px;
        }
        .anonymous-purchase-modal .modal-icon svg {
          width: 48px;
          height: 48px;
        }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.modal);
  }

  bindEvents() {
    // Prevent any interaction with the modal (it should be persistent)
    this.modal.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
    
    // Prevent escape key from closing
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  open(customMessage = null) {
    if (this.isOpen) return;
    
    this.isOpen = true;
    
    // Update message if provided (supports HTML)
    if (customMessage) {
      this.modal.querySelector('#modal-message').innerHTML = customMessage;
    }
    
    // Try to get and display shop logo
    this.displayShopLogo();
    
    // Add blur effect to page
    document.body.classList.add('anonymous-purchase-blocked');
    
    // Show backdrop and modal
    this.backdrop.classList.add('is-open');
    this.modal.classList.add('is-open');
    
    console.log('🚫 Anonymous purchase modal opened - shopping blocked');
  }

  displayShopLogo() {
    const modalLogo = this.modal.querySelector('#modal-logo');
    
    // Try to find the main site logo
    const siteLogo = document.querySelector('#header-logo, .header__heading-logo, .site-logo, [data-logo]');
    
    if (siteLogo && modalLogo) {
      const logoSrc = siteLogo.src || siteLogo.getAttribute('data-src');
      if (logoSrc) {
        modalLogo.src = logoSrc;
        modalLogo.alt = siteLogo.alt || 'Shop Logo';
        modalLogo.style.display = 'block';
      }
    }
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    
    // Remove blur effect from page
    document.body.classList.remove('anonymous-purchase-blocked');
    
    // Hide backdrop and modal
    this.backdrop.classList.remove('is-open');
    this.modal.classList.remove('is-open');
    
    console.log('✅ Anonymous purchase modal closed - shopping enabled');
  }

  // Validates that a value is a properly formatted GUID/UUID
  static isValidGuid(value) {
    if (!value || typeof value !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
      || /^[0-9a-f]{32}$/i.test(value);
  }

  // Static method to check if svsCustomerId exists and is a valid GUID
  static getSvsCustomerId() {
    // Use the existing logic from collection-groups.js if available
    if (window.collectionGroupsManager && typeof window.collectionGroupsManager.getCurrentSvsCustomerId === 'function') {
      const id = window.collectionGroupsManager.getCurrentSvsCustomerId();
      if (id && AnonymousPurchaseModal.isValidGuid(id)) return id;
    }

    // Fallback implementation matching the existing logic
    // First check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    let svsCustomerId = urlParams.get('svsCustomerId');

    if (svsCustomerId && svsCustomerId !== 'null') {
      const decoded = decodeURIComponent(svsCustomerId);
      if (AnonymousPurchaseModal.isValidGuid(decoded)) return decoded;
      console.warn('svsCustomerId from URL is not a valid GUID:', decoded);
    }

    // If not in URL, check localStorage svsCustomer data
    try {
      const svsCustomerData = localStorage.getItem('svsCustomer');
      if (svsCustomerData) {
        const customerData = JSON.parse(svsCustomerData);
        if (customerData && customerData.svsCustomerId && AnonymousPurchaseModal.isValidGuid(customerData.svsCustomerId)) {
          return customerData.svsCustomerId;
        }
      }
    } catch (error) {
      console.warn('Error reading svsCustomer from localStorage:', error);
    }

    // Check kit data (matching collection-groups.js logic)
    try {
      const kitsData = localStorage.getItem('athlete_kits');
      if (kitsData) {
        const parsed = JSON.parse(kitsData);
        if (parsed.svsCustomerId && AnonymousPurchaseModal.isValidGuid(parsed.svsCustomerId)) {
          return parsed.svsCustomerId;
        }
      }
    } catch (e) {
      console.warn('Error reading svsCustomerId from kit data:', e);
    }

    // Check legacy storage
    try {
      const legacyData = localStorage.getItem('collection_selected_group');
      if (legacyData) {
        const parsed = JSON.parse(legacyData);
        if (parsed.svsCustomerId && AnonymousPurchaseModal.isValidGuid(parsed.svsCustomerId)) {
          return parsed.svsCustomerId;
        }
      }
    } catch (e) {
      console.warn('Error reading svsCustomerId from legacy data:', e);
    }

    return null;
  }

  // Static method to check if anonymous purchase should be blocked
  static shouldBlockAnonymousPurchase(allowAnonymousPurchase = true) {
    // If allow_anonymous_purchase is true (default), never block
    if (allowAnonymousPurchase === true) {
      return false;
    }

    // If allow_anonymous_purchase is false, block only if no valid svsCustomerId
    if (allowAnonymousPurchase === false) {
      const svsCustomerId = AnonymousPurchaseModal.getSvsCustomerId();
      const shouldBlock = !svsCustomerId;

      console.log('🔍 Anonymous purchase check:', {
        allowAnonymousPurchase,
        svsCustomerId: svsCustomerId || 'not found (missing or invalid GUID)',
        shouldBlock
      });

      return shouldBlock;
    }

    return false;
  }

  // Called when the API fetch fails or returns no valid customer data.
  // Opens the modal if anonymous purchases are not allowed for this collection.
  static onSvsCustomerValidationFailed(reason) {
    console.warn('svsCustomerId validation failed:', reason);
    try { localStorage.removeItem('svsCustomer'); } catch(e) {}

    if (window.svsAllowAnonymousPurchase === false && window.anonymousPurchaseModal) {
      window.anonymousPurchaseModal.open(
        window.svsAnonymousPurchaseMessage ||
        '<p>Your personalized link is invalid or has expired. Please contact customer service for a new link.</p>'
      );
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.anonymousPurchaseModal = new AnonymousPurchaseModal();
  });
} else {
  window.anonymousPurchaseModal = new AnonymousPurchaseModal();
}

// Export for global access
window.AnonymousPurchaseModal = AnonymousPurchaseModal;

} // Close the if (!window.AnonymousPurchaseModal) block
